// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Waitlist OFFER brain (Jose 2026-08-06)
 * =============================================
 * A waitlist "offer" = the shop hand-picks up to 3 open slots across a client's preferred
 * days and TEXTS the client a link. The client sees a live 5-minute countdown and taps the
 * one they want; that claims it (first-come-first-served) and books the appointment. If the
 * timer runs out with no response, the offer dies and every slot releases.
 *
 * Pure logic + types only — NO React, NO Firebase (each app does its own Firestore I/O with
 * these types). Edit THIS file at repo root and run scripts/sync-shared.sh to propagate.
 * Do NOT edit the per-app copies in <app>/src/shared/.
 */

/** How long the client has to claim before the offer expires. Owner-tunable later. */
export const OFFER_HOLD_MINUTES = 5;

/** Max slots the operator can put in one offer. */
export const MAX_OFFER_OPTIONS = 3;

/** One proposed slot in an offer. */
export interface OfferOption {
  barberId: string;
  barberName: string;
  date: string;      // YYYY-MM-DD
  time: string;      // HH:MM (24h)
  duration: number;  // minutes
  price: number;
  service: string;
}

export type OfferStatus = 'sent' | 'claimed' | 'expired' | 'cancelled';

/**
 * Firestore `waitlist_offers` document (shared across all 3 apps).
 * Times are stored as epoch MILLIS (createdAtMs / expiresAtMs) so the client can do
 * countdown math without a Firestore Timestamp dependency on the read side.
 */
export interface WaitlistOffer {
  id: string;
  token: string;         // == doc id; the SMS claim link uses this
  entryId: string;       // the waitlist entry this came from
  clientId?: string;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  options: OfferOption[];
  status: OfferStatus;
  createdAtMs: number;
  expiresAtMs: number;
  holdMinutes: number;
  claimedOption?: number;   // index into options once claimed
  claimedAtMs?: number;
  // RESCHEDULE mode (Jose 2026-09-26): reuse the exact offer/claim pipeline to MOVE an existing
  // appointment instead of creating a new one. 'waitlist' (default) → claim books a NEW appt;
  // 'reschedule' → claim reschedules `appointmentId` (moves + reassigns), so no duplicate is created.
  mode?: 'waitlist' | 'reschedule';
  appointmentId?: string;   // the appointment to move, when mode === 'reschedule'
}

/** Stable identity of a slot — used for select/dedupe in the picker and for hold checks. */
export function optionKey(o: Pick<OfferOption, 'barberId' | 'date' | 'time'>): string {
  return `${o.barberId}|${o.date}|${o.time}`;
}

/** Is this offer still live and holding its slots? */
export function isOfferActive(o: Pick<WaitlistOffer, 'status' | 'expiresAtMs'>, nowMs: number): boolean {
  return o.status === 'sent' && nowMs < o.expiresAtMs;
}

/** Can the client still claim a slot from this offer right now? */
export function offerClaimable(o: Pick<WaitlistOffer, 'status' | 'expiresAtMs'>, nowMs: number): boolean {
  return isOfferActive(o, nowMs);
}

/** Whole seconds left on the countdown (never negative). */
export function secondsRemaining(o: Pick<WaitlistOffer, 'expiresAtMs'>, nowMs: number): number {
  return Math.max(0, Math.ceil((o.expiresAtMs - nowMs) / 1000));
}

/** "4:59" style mm:ss for the countdown display. */
export function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Slots currently HELD by active offers (so the operator isn't offered a slot that's already
 * out on another live offer). Soft hold: the hard guarantee is the double-book check at claim
 * time (first-come-first-served). Returns a Set of optionKey()s.
 */
export function heldSlotKeys(offers: WaitlistOffer[], nowMs: number): Set<string> {
  const held = new Set<string>();
  for (const o of offers) {
    if (!isOfferActive(o, nowMs)) continue;
    for (const opt of o.options) held.add(optionKey(opt));
  }
  return held;
}

/** "HH:MM" (24h, minutes-from-midnight) → "9:30 AM". */
export function offerTimeLabel(time: string): string {
  const [hStr, mStr] = time.split(':');
  const h24 = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h24)) return time;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** "2026-08-07" → "Fri, Aug 7". */
export function offerDayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
